import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import { getAllocations } from 'src/redux/actions/portfolioBuilderActions';
import { selectedWorkspacesSelector, portfolioAllocationsDataSelector } from 'src/redux/selectors';
import { PAGES } from 'src/constants/pages';
import { initialParams } from '../../PortfolioBuilderHomePage/constants';

/**
 * Hook to monitor selected workspace changes and check if current allocation
 * belongs to the selected workspace. If not, redirects to Portfolio Builder page.
 * @param {boolean} isViewPage - Whether the current page is in view mode
 * @returns {boolean} isLoading - Whether the hook is currently validating allocation
 */
const useWorkspaceAllocationCheck = (isViewPage: boolean): void => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { id } = useParams<{ id?: string }>();

  const [prevWorkspaceId, setPrevWorkspaceId] = useState<string | null>(null);

  const selectedWorkspace = useSelector(selectedWorkspacesSelector);
  const { allocations } = useSelector(portfolioAllocationsDataSelector);

  const currentWorkspaceId = useMemo(() => selectedWorkspace?.workspace?.id, [selectedWorkspace?.workspace?.id]);

  useEffect(() => {
    if (!isViewPage || !id) return;

    if (currentWorkspaceId !== prevWorkspaceId) {
      dispatch(getAllocations(initialParams));
    }
  }, [dispatch, id, isViewPage, currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    if (!isViewPage || !id) return;

    const allocationExists = !isEmpty(allocations) && allocations.some((allocation) => allocation.id === id);

    if (!allocationExists) {
      push(PAGES.PORTFOLIO_BUILDER);
    }

    setPrevWorkspaceId(currentWorkspaceId);
  }, [allocations, currentWorkspaceId, id, isViewPage, push]);
};

export default useWorkspaceAllocationCheck;
